import { VivElement, defineComponent } from './base.js';
import { escapeHtml } from '../core/htmlEscape.js';

/**
 * `<viv-modal title="…" [open] [dismissible]>…content…</viv-modal>`
 * Wraps arbitrary content in a themed backdrop + panel. Backdrop / close click
 * dispatches a cancelable `viv-close` event and closes unless prevented.
 */
export class VivModal extends VivElement {
    static get observedAttributes(): string[] {
        return ['title', 'open', 'dismissible'];
    }

    private panel: HTMLElement | null = null;
    private bodyHost: HTMLElement | null = null;
    private savedContent: DocumentFragment | null = null;

    protected render(): void {
        const title = this.attr('title');
        const dismissible = this.hasFlag('dismissible') || !this.hasAttribute('dismissible');
        const open = this.hasFlag('open');

        if (!this.panel) {
            this.savedContent = document.createDocumentFragment();
            while (this.firstChild) this.savedContent.appendChild(this.firstChild);

            this.className = 'vivModal';
            this.innerHTML = `
                <div class="vivModal__backdrop" data-viv-modal-dismiss></div>
                <div class="vivModal__panel" role="dialog" aria-modal="true">
                    ${dismissible ? '<button type="button" class="vivModal__close" aria-label="Schließen" data-viv-modal-dismiss><i class="fas fa-times" aria-hidden="true"></i></button>' : ''}
                    <div class="vivModal__header"></div>
                    <div class="vivModal__body"></div>
                </div>`;
            this.panel = this.querySelector('.vivModal__panel');
            this.bodyHost = this.querySelector('.vivModal__body');
            if (this.bodyHost && this.savedContent) this.bodyHost.appendChild(this.savedContent);

            this.addEventListener('click', (event) => {
                const target = event.target as HTMLElement;
                if (target.closest('[data-viv-modal-dismiss]')) this.requestClose();
            });
        }

        const header = this.querySelector('.vivModal__header');
        if (header) header.innerHTML = title ? `<h2 class="vivModal__title">${escapeHtml(title)}</h2>` : '';
        this.toggleAttribute('open', open);
    }

    private requestClose(): void {
        const event = new CustomEvent('viv-close', { bubbles: true, cancelable: true });
        const proceed = this.dispatchEvent(event);
        if (proceed) this.close();
    }

    open(): void {
        this.setAttribute('open', '');
    }

    close(): void {
        this.removeAttribute('open');
    }
}

export function registerVivModal(): void {
    defineComponent('viv-modal', VivModal);
}
